/**
 * SegmentedControl — the iOS-style pill switch used across the phone app.
 *
 * Extracted because three copies appeared within one feature: the invoice date
 * filter, the bookings Upcoming/Recent switch, and the package vs single-service
 * toggle in the booking form. All three were the same markup with the same active
 * styling, which is exactly the thing that drifts — one gains a focus ring, another
 * a different grey, and the app stops looking like one app.
 *
 * Deliberately presentational and stateless: it renders the options it is given and
 * reports taps. Which option is active, and what selecting one does, belongs to the
 * screen — a control that owned its own selection could not be driven by a filter
 * that also changes from an empty-state button.
 */

import React from 'react';

/**
 * @param {{
 *   options: Array<{ key: string, label: React.ReactNode, badge?: React.ReactNode }>,
 *   value: string,
 *   onChange: (key: string) => void,
 *   disabled?: boolean,
 *   ariaLabel?: string,
 * }} props
 */
const SegmentedControl = ({ options, value, onChange, disabled = false, ariaLabel }) => (
    // `group` rather than `radiogroup`: the buttons are aria-pressed toggles, and a
    // radiogroup would promise arrow-key roving that this does not implement.
    <div role="group" aria-label={ariaLabel} className="flex gap-1 rounded-xl bg-gray-100 p-1">
        {options.map(({ key, label, badge }) => {
            const active = value === key;
            return (
                <button
                    key={key}
                    type="button"
                    disabled={disabled}
                    aria-pressed={active}
                    onClick={() => onChange(key)}
                    className={`flex-1 rounded-lg py-2 text-xs font-semibold transition-colors disabled:opacity-50 ${
                        active ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
                    }`}
                >
                    {label}
                    {badge !== undefined && badge !== null && (
                        <span className="ml-1 font-normal text-gray-400">{badge}</span>
                    )}
                </button>
            );
        })}
    </div>
);

export default SegmentedControl;
